import {
  AlertCircle,
  BookOpen01,
  ChevronRight,
  Mail01,
  MessageChatCircle,
  MessageChatSquare,
} from '@untitledui/icons';
import { useCallback } from 'react';

import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';

// =============================================================================
// Module-scope constants
// =============================================================================

const SUPPORT_EMAIL = 'support@ahoy.ai';
const SUPPORT_DOCS_URL = 'https://support.ahoy.ai/';
const SUPPORT_PHONE_DISPLAY = '+1 (628) 289 6238';
const SUPPORT_PHONE_TEL = '+16282896238';

const HELP_DESCRIPTION_WITH_SLACK =
  'Our team is ready to help. Get quick responses directly from our CEO via text or Slack or send us an email for more detailed inquiries.';
const HELP_DESCRIPTION_NO_SLACK =
  'Our team is ready to help. Get quick responses directly from our CEO via text or send us an email for more detailed inquiries.';

const TEXT_CEO_WAIT = '3-5 min wait';
const EMAIL_SUPPORT_WAIT = '1-3 hr wait';

// Body wrapper: tightens the description→first-row gap and adds a bottom
// pad that mirrors the Dialog's horizontal px-6 so the modal feels balanced.
const BODY_CLASS = 'flex flex-col gap-0.5 pt-0.5 pb-6';

// Whole-row link: icon | (label / sub-value) | dot + wait + chevron.
// Negative -mx-2 lets the hover background bleed slightly under the dialog
// padding so the click target feels generous without changing the dialog
// chrome itself. Vertical py-3 gives the bold label + sub-value enough room
// to breathe.
const ROW_CLASS =
  'group -mx-2 flex items-center gap-4 rounded-lg px-2 py-3 transition-colors hover:bg-bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-primary/30';

const ICON_CLASS = 'h-6 w-6 flex-shrink-0 text-fg-primary';
const LABEL_CLASS = 'text-sm font-semibold text-fg-primary leading-tight';
const SUBVALUE_CLASS = 'mt-0.5 text-sm text-fg-secondary leading-tight';
const META_CLASS = 'flex flex-shrink-0 items-center gap-2';
const WAIT_TEXT_CLASS = 'text-sm text-fg-secondary';
const CHEVRON_CLASS =
  'h-4 w-4 text-fg-tertiary transition-transform group-hover:translate-x-0.5';
// Use the design-system success token directly so the dot stays vivid in both themes.
const DOT_CLASS = 'h-2 w-2 rounded-full bg-[var(--color-success-500)]';

// =============================================================================
// Types
// =============================================================================

export interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When non-empty, a Slack row links to this URL. When empty/null/undefined, the row is NOT rendered. */
  slackChannelUrl?: string | null;
  /** When provided, a "Report a problem" row is rendered that calls this handler. */
  onReportProblem?: () => void;
}

// =============================================================================
// HelpModal
// =============================================================================

export function HelpModal({
  isOpen,
  onClose,
  slackChannelUrl,
  onReportProblem,
}: HelpModalProps) {
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  const showSlack =
    typeof slackChannelUrl === 'string' && slackChannelUrl.length > 0;

  const description = showSlack
    ? HELP_DESCRIPTION_WITH_SLACK
    : HELP_DESCRIPTION_NO_SLACK;

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal className='max-w-xl'>
        <Dialog title='Help & Feedback' description={description}>
          <div className={BODY_CLASS}>
            {/* Text the CEO */}
            <a href={`tel:${SUPPORT_PHONE_TEL}`} className={ROW_CLASS}>
              <MessageChatSquare className={ICON_CLASS} aria-hidden='true' />
              <div className='min-w-0 flex-1'>
                <div className={LABEL_CLASS}>Text the CEO</div>
                <div className={SUBVALUE_CLASS}>{SUPPORT_PHONE_DISPLAY}</div>
              </div>
              <div className={META_CLASS}>
                <span className={DOT_CLASS} aria-hidden='true' />
                <span className={WAIT_TEXT_CLASS}>{TEXT_CEO_WAIT}</span>
                <ChevronRight className={CHEVRON_CLASS} aria-hidden='true' />
              </div>
            </a>

            {/* Email support */}
            <a href={`mailto:${SUPPORT_EMAIL}`} className={ROW_CLASS}>
              <Mail01 className={ICON_CLASS} aria-hidden='true' />
              <div className='min-w-0 flex-1'>
                <div className={LABEL_CLASS}>Email support</div>
                <div className={SUBVALUE_CLASS}>{SUPPORT_EMAIL}</div>
              </div>
              <div className={META_CLASS}>
                <span className={DOT_CLASS} aria-hidden='true' />
                <span className={WAIT_TEXT_CLASS}>{EMAIL_SUPPORT_WAIT}</span>
                <ChevronRight className={CHEVRON_CLASS} aria-hidden='true' />
              </div>
            </a>

            {/* Support docs — always rendered */}
            <a
              href={SUPPORT_DOCS_URL}
              target='_blank'
              rel='noopener noreferrer'
              className={ROW_CLASS}
            >
              <BookOpen01 className={ICON_CLASS} aria-hidden='true' />
              <div className='min-w-0 flex-1'>
                <div className={LABEL_CLASS}>Support docs</div>
                <div className={SUBVALUE_CLASS}>Guides, FAQs &amp; how-tos</div>
              </div>
              <div className={META_CLASS}>
                <ChevronRight className={CHEVRON_CLASS} aria-hidden='true' />
              </div>
            </a>

            {/* Slack — only rendered when slackChannelUrl is a non-empty string */}
            {showSlack && (
              <a
                href={slackChannelUrl as string}
                target='_blank'
                rel='noopener noreferrer'
                className={ROW_CLASS}
              >
                <MessageChatCircle className={ICON_CLASS} aria-hidden='true' />
                <div className='min-w-0 flex-1'>
                  <div className={LABEL_CLASS}>Slack</div>
                  <div className={SUBVALUE_CLASS}>Open shared channel</div>
                </div>
                <div className={META_CLASS}>
                  <ChevronRight className={CHEVRON_CLASS} aria-hidden='true' />
                </div>
              </a>
            )}

            {/* Report a problem — only rendered when onReportProblem is supplied */}
            {onReportProblem && (
              <button
                type='button'
                onClick={onReportProblem}
                className={ROW_CLASS}
              >
                <AlertCircle className={ICON_CLASS} aria-hidden='true' />
                <div className='min-w-0 flex-1'>
                  <div className={LABEL_CLASS}>Report a problem</div>
                  <div className={SUBVALUE_CLASS}>Send diagnostics to Ahoy</div>
                </div>
                <div className={META_CLASS}>
                  <ChevronRight className={CHEVRON_CLASS} aria-hidden='true' />
                </div>
              </button>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
